import { useCallback } from "react";

/**
 * Configuration options for useLocalStorage hook.
 */
export interface UseLocalStorageOptions<T> {
  /**
   * Custom serialization function. Defaults to JSON.stringify.
   */
  serialize?: (value: T) => string;

  /**
   * Custom deserialization function. Defaults to JSON.parse.
   */
  deserialize?: (value: string) => T;

  /**
   * Whether to log errors to console. Defaults to false (silent failures).
   * Set to true during development for debugging.
   */
  logErrors?: boolean;
}

/**
 * Return type for useLocalStorage hook.
 */
export interface UseLocalStorageReturn<T> {
  /**
   * Save data to localStorage with the specified key.
   */
  save: (value: T) => void;

  /**
   * Load data from localStorage. Returns null if not found or on error.
   */
  load: () => T | null;

  /**
   * Remove data from localStorage.
   */
  remove: () => void;

  /**
   * The storage key being used.
   */
  key: string;
}

/**
 * Generic hook for localStorage operations with type safety and error handling.
 *
 * This hook provides a clean, type-safe interface for interacting with localStorage.
 * It handles serialization, deserialization, and error handling automatically.
 *
 * **Features:**
 * - Type-safe storage operations
 * - Automatic JSON serialization/deserialization
 * - Silent error handling by default (won't crash on storage failures)
 * - Custom serialization support
 * - SSR-safe (won't crash on server)
 *
 * **Error Handling:**
 * All storage operations are wrapped in try-catch blocks to handle:
 * - Storage quota exceeded errors
 * - Private browsing mode restrictions
 * - Invalid JSON parsing
 * - Security exceptions
 *
 * @template T The type of data being stored
 * @param key The localStorage key to use for storage
 * @param options Optional configuration for serialization and error handling
 *
 * @example
 * ```tsx
 * // Basic usage with objects
 * interface UserPrefs {
 *   theme: string;
 *   language: string;
 * }
 *
 * const { save, load, remove } = useLocalStorage<UserPrefs>('user-prefs');
 *
 * // Save
 * save({ theme: 'dark', language: 'en' });
 *
 * // Load
 * const prefs = load(); // UserPrefs | null
 * if (prefs) {
 *   console.log(prefs.theme);
 * }
 *
 * // Remove
 * remove();
 * ```
 *
 * @example
 * ```tsx
 * // With custom serialization (e.g., compression)
 * const { save, load } = useLocalStorage<string>('compressed-data', {
 *   serialize: (val) => compressString(val),
 *   deserialize: (val) => decompressString(val),
 *   logErrors: true // Enable error logging in development
 * });
 * ```
 *
 * @example
 * ```tsx
 * // SSR-safe usage (Next.js, etc.)
 * const { save, load } = useLocalStorage<Settings>('settings');
 *
 * useEffect(() => {
 *   // Only access localStorage on client
 *   const settings = load();
 *   if (settings) {
 *     setSettings(settings);
 *   }
 * }, []);
 * ```
 */
export function useLocalStorage<T>(
  key: string,
  options: UseLocalStorageOptions<T> = {}
): UseLocalStorageReturn<T> {
  const {
    serialize = JSON.stringify,
    deserialize = JSON.parse,
    logErrors = false
  } = options;

  /**
   * Save data to localStorage.
   * Fails silently if storage is unavailable or quota is exceeded.
   */
  const save = useCallback(
    (value: T): void => {
      try {
        const serialized = serialize(value);
        localStorage.setItem(key, serialized);
      } catch (error) {
        if (logErrors) {
          console.error(`[useLocalStorage] Failed to save data for key "${key}":`, error);
        }
      }
    },
    [key, serialize, logErrors]
  );

  /**
   * Load data from localStorage.
   * Returns null if not found or on deserialization error.
   */
  const load = useCallback((): T | null => {
    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return null;
      }
      return deserialize(item);
    } catch (error) {
      if (logErrors) {
        console.error(`[useLocalStorage] Failed to load data for key "${key}":`, error);
      }
      return null;
    }
  }, [key, deserialize, logErrors]);

  /**
   * Remove data from localStorage.
   * Fails silently if storage is unavailable.
   */
  const remove = useCallback((): void => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      if (logErrors) {
        console.error(`[useLocalStorage] Failed to remove data for key "${key}":`, error);
      }
    }
  }, [key, logErrors]);

  return {
    save,
    load,
    remove,
    key
  };
}
